import { useSession } from '../../state/session.ts';

// What the signed-in person may do in a workspace or organization. The backend decides and sends
// `permissions`; the UI only hides controls it would refuse. Role strings are compared here only.

export type Permission =
  | 'workspace.read'
  | 'workspace.write'
  | 'workspace.members.manage'
  | 'workspace.settings.manage'
  | 'workspace.context.manage'
  | 'workspace.authorization.manage'
  | 'org.read'
  | 'org.members.manage'
  | 'org.workspaces.read_all'
  | 'org.workspaces.create'
  | 'org.settings.manage';

interface Grant {
  role?: string | undefined;
  permissions?: string[] | undefined;
}

export function can(subject: Grant | null | undefined, permission: Permission): boolean {
  if (!subject) return false;
  if (subject.permissions) return subject.permissions.includes(permission);
  // Saved before permissions existed: an owner keeps everything, anyone else only reads. No role
  // at all is a setup session approved by email, whose owner token the backend checks.
  return !subject.role || isOwner(subject) || permission.endsWith('.read');
}

/** Owner rows are never changed by admins, and only an org owner grants or removes owner. */
export const isOwner = (subject: Grant | null | undefined) => subject?.role === 'owner';

export const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

/** Whether the open workspace lets the signed-in person do this. */
export const useCan = (permission: Permission) => useSession((s) => can(s.workspace, permission));
